import { ScoreColorVariant } from '@vizit/constants';
import { cva } from '@vizit/theme';
import { ScoreStickerV2Size } from './types';

const sizeClasses: Record<ScoreStickerV2Size, string> = {
  [ScoreStickerV2Size.Lg]: 'h-53 w-53',
  [ScoreStickerV2Size.Sm]: 'h-32 w-32',
  [ScoreStickerV2Size.Xs]: 'h-20 w-20',
  [ScoreStickerV2Size.Xxs]: 'h-16 w-16',
};

// Informational variant: neutral gray sticker with a centered text-lines icon.
const informationalStickerClasses = cva({
  base: 'flex items-center justify-center shrink-0 bg-gray-utility-200',
  variants: {
    size: sizeClasses,
  },
});

// Icon size (px) per sticker size, scaled to match the score glyph footprint.
const informationalIconSizes: Record<ScoreStickerV2Size, number> = {
  [ScoreStickerV2Size.Lg]: 40, // score-threshold-allow: icon size in px
  [ScoreStickerV2Size.Sm]: 24,
  [ScoreStickerV2Size.Xs]: 16,
  [ScoreStickerV2Size.Xxs]: 12,
};

const stickerClasses = cva({
  base: 'relative flex items-center justify-center shrink-0',
  variants: {
    colorVariant: {
      [ScoreColorVariant.NotApplicable]: 'bg-score-not-applicable',
      [ScoreColorVariant.VeryLow]: 'bg-score-very-low',
      [ScoreColorVariant.Low]: 'bg-score-low',
      [ScoreColorVariant.Moderate]: 'bg-score-moderate',
      [ScoreColorVariant.High]: 'bg-score-high',
      [ScoreColorVariant.VeryHigh]: 'bg-score-very-high',
    },
    size: sizeClasses,
    isDisabled: {
      true: 'bg-transparent',
      false: '',
    },
    isProcessing: {
      true: '',
      false: '',
    },
  },
  compoundVariants: [
    // Processing keeps the tier's solid surface for every tier except Optimized,
    // whose success-200 surface is too faint under the component's 40% opacity, so
    // it gets a more saturated success-300 — but only when not disabled, since
    // disabled forces a transparent surface.
    { isProcessing: true, isDisabled: false, colorVariant: ScoreColorVariant.VeryHigh, className: 'bg-success-300' },
  ],
});

const scoreTextClasses = cva({
  base: 'text-center font-mono leading-none',
  variants: {
    colorVariant: {
      [ScoreColorVariant.NotApplicable]: 'text-score-not-applicable-strong',
      [ScoreColorVariant.VeryLow]: 'text-score-very-low-strong',
      [ScoreColorVariant.Low]: 'text-score-low-strong',
      [ScoreColorVariant.Moderate]: 'text-score-moderate-strong',
      [ScoreColorVariant.High]: 'text-score-high-strong',
      [ScoreColorVariant.VeryHigh]: 'text-score-very-high-strong',
    },
    size: {
      [ScoreStickerV2Size.Lg]: 'text-display-lg font-bold',
      [ScoreStickerV2Size.Sm]: 'text-display-md font-bold',
      [ScoreStickerV2Size.Xs]: 'text-xl font-medium',
      [ScoreStickerV2Size.Xxs]: 'text-sm font-medium',
    },
    isNa: {
      true: '',
      false: '',
    },
    isDisabled: {
      true: '',
      false: '',
    },
  },
  compoundVariants: [
    // N/A on Sm uses smaller text since "N/A" is wider than digits
    {
      isNa: true,
      size: ScoreStickerV2Size.Sm,
      className: 'text-display-xs',
    },
    // Disabled / muted state: text fades to the tier family's light (200) shade.
    {
      isDisabled: true,
      colorVariant: ScoreColorVariant.NotApplicable,
      className: 'text-gray-utility-300',
    },
    {
      isDisabled: true,
      colorVariant: ScoreColorVariant.VeryLow,
      className: 'text-error-200',
    },
    {
      isDisabled: true,
      colorVariant: ScoreColorVariant.Low,
      className: 'text-error-200',
    },
    {
      isDisabled: true,
      colorVariant: ScoreColorVariant.Moderate,
      className: 'text-orange-200',
    },
    {
      isDisabled: true,
      colorVariant: ScoreColorVariant.High,
      className: 'text-yellow-200',
    },
    {
      isDisabled: true,
      colorVariant: ScoreColorVariant.VeryHigh,
      className: 'text-success-200',
    },
  ],
});

export { sizeClasses, scoreTextClasses, stickerClasses, informationalStickerClasses, informationalIconSizes };
